import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { api, fileUrl } from "@/lib/api";
import PhotoMarkupModal from "@/components/PhotoMarkupModal";
import {
  ArrowLeft, CloudArrowUp, GoogleLogo, HardDrives, Sparkle,
  Pencil, Trash, Question, PaperPlaneTilt, House, ChatCircleDots, Plus,
  CheckCircle, FilePdf, LinkSimple, Envelope,
} from "@phosphor-icons/react";

/**
 * PropertyInspection — the "Inspection Radar" page for a property.
 *
 * Wizard steps:
 *   1. Import — pick photos: (a) device multi-select, (b) Google Photos
 *      share URL scrape (beta), (c) Google Drive folder (needs API key).
 *   2. Tag — assign a room to every photo (fixed 17-room list + custom).
 *   3. Analyze — Claude Vision returns findings, condition scores (A-F),
 *      appliance nameplates, and a bid draft.
 *   4. Review — browse findings by room, ask Q&A, spawn a WO from any
 *      finding with one tap.
 *
 * All state lives on the server; the client just paints the current
 * snapshot. That way multiple PMs can work the same inspection in
 * parallel and background AI runs don't lose progress.
 */

const IMPORT_METHODS = [
  { k: "device",   label: "Upload from device",  icon: CloudArrowUp,  hint: "Select up to 200 photos from your phone or computer" },
  { k: "gphotos",  label: "Google Photos link",  icon: GoogleLogo,     hint: "Paste photos.app.goo.gl/... URL · Beta best-effort" },
  { k: "gdrive",   label: "Google Drive folder", icon: HardDrives,     hint: "Paste drive.google.com/drive/folders/... URL · Most reliable" },
];

const CATEGORY_COLORS = {
  plumbing: "bg-blue-100 text-blue-700",
  electrical: "bg-amber-100 text-amber-700",
  drywall: "bg-slate-100 text-slate-700",
  paint: "bg-fuchsia-100 text-fuchsia-700",
  flooring: "bg-orange-100 text-orange-700",
  appliance: "bg-emerald-100 text-emerald-700",
  hvac: "bg-cyan-100 text-cyan-700",
  exterior: "bg-lime-100 text-lime-700",
  other: "bg-slate-100 text-slate-600",
};
const SEVERITY_COLORS = {
  minor: "bg-slate-200 text-slate-700",
  moderate: "bg-amber-200 text-amber-800",
  major: "bg-red-200 text-red-800",
};
const SCORE_COLORS = {
  A: "bg-emerald-500 text-white",
  B: "bg-lime-400 text-slate-900",
  C: "bg-amber-400 text-slate-900",
  D: "bg-orange-500 text-white",
  F: "bg-red-600 text-white",
};

export default function PropertyInspection() {
  const { iid } = useParams();
  const navigate = useNavigate();
  const [insp, setInsp] = useState(null);
  const [fixedRooms, setFixedRooms] = useState([]);
  const [tab, setTab] = useState("import");
  const [busy, setBusy] = useState(false);
  const [importMethod, setImportMethod] = useState("device");
  const [gUrl, setGUrl] = useState("");
  const [analyzing, setAnalyzing] = useState(false);
  const [autoTagging, setAutoTagging] = useState(false);
  const [ask, setAsk] = useState("");
  const [asking, setAsking] = useState(false);
  const [checklistBusy, setChecklistBusy] = useState(false);
  const [recsBusy, setRecsBusy] = useState(false);
  const [markupPhoto, setMarkupPhoto] = useState(null);
  const fileInputRef = useRef(null);

  const initialLoadDone = useRef(false);
  const load = async () => {
    try {
      const { data } = await api.get(`/prop-inspections/${iid}`);
      setInsp(data.inspection);
      setFixedRooms(data.fixed_rooms);
      // Auto-advance based on state — only on the very first load, so
      // hitting Complete (or re-loading in place) doesn't yank the user
      // out of the tab they're on.
      if (initialLoadDone.current) return;
      initialLoadDone.current = true;
      if (data.inspection.status === "complete" || data.inspection.status === "completed") setTab("recommend");
      else if (data.inspection.status === "analyzing") { setTab("tag"); setAnalyzing(true); pollAnalyzeUntilDone(); }
      else if ((data.inspection.photos || []).length > 0) setTab("tag");
      else setTab("import");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't load inspection");
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [iid]);

  const photos = insp?.photos || [];
  const rooms = [...(fixedRooms || []), ...(insp?.custom_rooms || [])];
  const untaggedCount = photos.filter(p => !p.room).length;
  const findingsByRoom = useMemo(() => {
    const map = {};
    (insp?.findings || []).forEach(f => { (map[f.room] = map[f.room] || []).push(f); });
    return map;
  }, [insp]);

  // ── Import handlers ────────────────────────────────────────────
  const uploadDevice = async (files) => {
    if (!files || files.length === 0) return;
    setBusy(true);
    let uploaded = 0;
    for (const f of files) {
      try {
        const fd = new FormData();
        fd.append("file", f);
        await api.post(`/prop-inspections/${iid}/photo`, fd, { headers: { "Content-Type": "multipart/form-data" } });
        uploaded++;
      } catch (e) { /* keep going, we surface count at end */ }
    }
    setBusy(false);
    toast.success(`${uploaded} photo${uploaded === 1 ? "" : "s"} added`);
    await load();
    if (uploaded > 0) setTab("tag");
  };
  const importGooglePhotos = async () => {
    if (!gUrl.trim()) { toast.error("Paste a Google Photos share URL"); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/prop-inspections/${iid}/import/google-photos`, { url: gUrl.trim() });
      toast.success(`Imported ${data.imported} of ${data.found} photos${data.beta_note ? " · beta" : ""}`);
      setGUrl("");
      await load();
      setTab("tag");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Google Photos import failed");
    } finally { setBusy(false); }
  };
  const importGoogleDrive = async () => {
    if (!gUrl.trim()) { toast.error("Paste a Google Drive folder URL"); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/prop-inspections/${iid}/import/google-drive`, { url: gUrl.trim() });
      toast.success(`Imported ${data.imported} of ${data.found} photos`);
      setGUrl("");
      await load();
      setTab("tag");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Drive import failed");
    } finally { setBusy(false); }
  };

  // ── Room tagging ───────────────────────────────────────────────
  const tagPhoto = async (fid, room) => {
    setInsp(prev => prev && { ...prev, photos: prev.photos.map(p => p.file_id === fid ? { ...p, room } : p) });
    try {
      await api.patch(`/prop-inspections/${iid}/photo/${fid}/room`, { room });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Tag failed");
      load();
    }
  };
  const bulkTag = async (room) => {
    const targets = photos.filter(p => !p.room);
    if (targets.length === 0) return;
    if (!window.confirm(`Tag all ${targets.length} untagged photos as "${room}"?`)) return;
    setBusy(true);
    await Promise.all(targets.map(p => api.patch(`/prop-inspections/${iid}/photo/${p.file_id}/room`, { room }).catch(() => null)));
    setBusy(false);
    await load();
  };
  const removePhoto = async (fid) => {
    if (!window.confirm("Remove this photo from the inspection?")) return;
    try { await api.delete(`/prop-inspections/${iid}/photo/${fid}`); await load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
  };
  const addCustomRoom = async () => {
    const n = window.prompt("Custom room name (e.g. Sunroom)");
    if (!n?.trim()) return;
    try {
      const { data } = await api.post(`/prop-inspections/${iid}/rooms`, { name: n.trim() });
      toast.success(`Added ${n.trim()}`);
      setInsp(prev => prev && { ...prev, custom_rooms: data.custom_rooms });
    } catch (e) { toast.error(e?.response?.data?.detail || "Add failed"); }
  };

  // ── AI Auto-tag rooms (Claude Haiku — cheap + fast) ───────────
  const autoTag = async () => {
    if (autoTagging) return;
    if (untaggedCount === 0) { toast("Every photo already has a room"); return; }
    setAutoTagging(true);
    try {
      const { data } = await api.post(`/prop-inspections/${iid}/auto-tag-rooms`, {
        confidence_threshold: 70,
        only_untagged: true,
      });
      const saved = Math.max(0, data.tagged) * 5; // ~5 sec per manual tag
      const min = Math.floor(saved / 60);
      const sec = saved % 60;
      toast.success(
        `🪄 Tagged ${data.tagged} · ${data.skipped_low_confidence} still need your input${saved ? ` · saved ~${min}m ${sec}s` : ""}`,
        { duration: 6000 }
      );
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Auto-tag failed");
    } finally {
      setAutoTagging(false);
    }
  };

  // ── AI Analyze ─────────────────────────────────────────────────
  // Runs in the background — POST returns 202 immediately, then the
  // status field on the inspection flips 'analyzing' → 'complete' (or
  // back to 'tagging' with `analyze_error` on failure). We poll every
  // 3s while status === 'analyzing'.
  const analyze = async () => {
    const tagged = photos.filter(p => p.room).length;
    if (tagged === 0) { toast.error("Tag at least one photo first"); return; }
    if (untaggedCount > 0 && !window.confirm(`${untaggedCount} photo(s) don't have a room and will be skipped. Continue?`)) return;
    setAnalyzing(true);
    try {
      await api.post(`/prop-inspections/${iid}/analyze`);
      toast("🧠 AI analysis started — this takes 30-90s for large jobs");
      // Kick off a poller that watches the inspection's status
      pollAnalyzeUntilDone();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "AI analysis failed");
      setAnalyzing(false);
    }
  };

  const pollAnalyzeUntilDone = async () => {
    const started = Date.now();
    const TIMEOUT_MS = 4 * 60 * 1000; // give it 4 min tops
    while (Date.now() - started < TIMEOUT_MS) {
      await new Promise(r => setTimeout(r, 3000));
      try {
        const { data } = await api.get(`/prop-inspections/${iid}`);
        const i = data.inspection;
        setInsp(i);
        if (i.analyze_error) {
          toast.error(`Analyze failed: ${i.analyze_error}`);
          setAnalyzing(false);
          return;
        }
        if (i.status === "complete") {
          toast.success(`AI found ${(i.findings || []).length} issue${(i.findings || []).length === 1 ? "" : "s"} across ${Object.keys(i.room_scores || {}).length} rooms`);
          setAnalyzing(false);
          setTab("review");
          return;
        }
      } catch (e) { /* transient network — keep polling */ }
    }
    toast.error("Analysis is taking longer than 4 minutes — refresh in a bit to check");
    setAnalyzing(false);
  };

  // ── Q&A ────────────────────────────────────────────────────────
  const askAI = async (e) => {
    e?.preventDefault?.();
    if (!ask.trim() || asking) return;
    setAsking(true);
    try {
      await api.post(`/prop-inspections/${iid}/ask`, { question: ask.trim() });
      setAsk("");
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Ask failed");
    } finally { setAsking(false); }
  };

  const spawnWO = async (fid) => {
    if (!window.confirm("Create a Work Order from this finding? Photos will be attached automatically.")) return;
    try {
      const { data } = await api.post(`/prop-inspections/${iid}/findings/${fid}/spawn-wo`);
      toast.success("Work Order created");
      // Open the new WO in a new tab so the user doesn't lose their place
      window.open(`/admin/jobs/${data.wo_id}`, "_blank");
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Spawn failed"); }
  };

  // ── Checklist (AI-drafted, PM confirms) ────────────────────────
  const genChecklist = async () => {
    if (checklistBusy) return;
    setChecklistBusy(true);
    try {
      await api.post(`/prop-inspections/${iid}/checklist/generate`);
      toast("🧠 AI checklist drafting — 30-90s");
      pollUntil(i => i.checklist_status === "ready" || i.checklist_status === "error");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Checklist failed");
      setChecklistBusy(false);
    }
  };
  const patchCell = async (room, item, patch) => {
    setInsp(prev => {
      if (!prev) return prev;
      const cl = { ...(prev.checklist || {}) };
      cl[room] = { ...(cl[room] || {}) };
      cl[room][item] = { ...(cl[room][item] || {}), ...patch };
      return { ...prev, checklist: cl };
    });
    try {
      await api.patch(`/prop-inspections/${iid}/checklist`, { updates: [{ room, item, ...patch }] });
    } catch (e) { toast.error("Save failed"); }
  };

  // ── Recommended turnover actions ───────────────────────────────
  const genRecs = async () => {
    if (recsBusy) return;
    setRecsBusy(true);
    try {
      await api.post(`/prop-inspections/${iid}/recommendations/generate`);
      toast("🧠 AI drafting turnover actions — 30-60s");
      pollUntil(i => i.recommendations_status === "ready" || i.recommendations_status === "error");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Recommendations failed");
      setRecsBusy(false);
    }
  };
  const spawnRecWO = async (rid) => {
    if (!window.confirm("Create a Work Order for this turnover action?")) return;
    try {
      const { data } = await api.post(`/prop-inspections/${iid}/recommendations/${rid}/spawn-wo`);
      toast.success("Work Order created");
      window.open(`/admin/jobs/${data.wo_id}`, "_blank");
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Spawn failed"); }
  };

  // ── Complete inspection (soft-lock + share token) ─────────────
  const [completing, setCompleting] = useState(false);
  const completeInspection = async () => {
    if (completing) return;
    const already = insp?.status === "completed";
    const msg = already
      ? "Save a new snapshot of the current inspection state?"
      : "Mark this inspection as complete and generate the report?";
    if (!window.confirm(msg)) return;
    setCompleting(true);
    try {
      await api.post(`/prop-inspections/${iid}/complete`);
      toast.success(already ? "New snapshot saved" : "Inspection completed ✓");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Complete failed");
    } finally { setCompleting(false); }
  };
  const shareUrl = insp?.share_token
    ? `${window.location.origin}/public/inspection/${insp.share_token}`
    : "";
  const openReport = () => {
    // Admin, full report — pass token via ?auth= (matches other PDF exports)
    const token = localStorage.getItem("access_token") || "";
    const base = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
    window.open(`${base}/api/prop-inspections/${iid}/report.pdf?auth=${encodeURIComponent(token)}`, "_blank");
  };
  const copyShareLink = async () => {
    if (!shareUrl) return;
    try {
      await navigator.clipboard.writeText(shareUrl);
      toast.success("Public link copied");
    } catch { toast.error("Copy failed — long-press to grab manually"); }
  };
  const emailLandlord = () => {
    if (!shareUrl) return;
    const subj = encodeURIComponent(`Inspection report — ${insp.title || "property"}`);
    const body = encodeURIComponent(`Hi,\n\nHere's the property inspection report:\n${shareUrl}\n\n(Photos + room-by-room conditions + recommended work.)`);
    window.location.href = `mailto:?subject=${subj}&body=${body}`;
  };

  // Generic poller — sleeps 3s and re-fetches until `done(i)` returns true
  const pollUntil = async (done) => {
    const start = Date.now();
    while (Date.now() - start < 4 * 60_000) {
      await new Promise(r => setTimeout(r, 3000));
      try {
        const { data } = await api.get(`/prop-inspections/${iid}`);
        setInsp(data.inspection);
        if (done(data.inspection)) {
          setChecklistBusy(false); setRecsBusy(false);
          return;
        }
      } catch (e) {}
    }
    setChecklistBusy(false); setRecsBusy(false);
  };

  if (!insp) {
    return <div className="min-h-screen bg-slate-100 flex items-center justify-center text-slate-500 font-black uppercase tracking-widest text-xs">Loading…</div>;
  }

  return (
    <div className="min-h-screen bg-slate-100 pb-24" data-testid="property-inspection-page">
      <header className="bg-white shadow-sm sticky top-0 z-30">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center gap-3">
          <button onClick={() => navigate(-1)} data-testid="insp-back"
            className="h-9 w-9 rounded-full hover:bg-slate-100 flex items-center justify-center">
            <ArrowLeft size={18}/>
          </button>
          <div className="flex-1 min-w-0">
            <div className="text-[10px] font-black uppercase tracking-widest text-slate-500">Inspection Radar</div>
            <div className="text-base font-black text-[#0F1F3A] truncate" data-testid="insp-title">{insp.title}</div>
          </div>
          <StatusPill status={insp.status}/>
        </div>
        <nav className="max-w-6xl mx-auto px-4 flex gap-1 text-[11px] font-black uppercase tracking-widest overflow-x-auto">
          {[
            { k: "import", label: `1 · Import (${photos.length})` },
            { k: "tag",    label: `2 · Tag${untaggedCount > 0 ? ` · ${untaggedCount} left` : " · done"}` },
            { k: "review", label: `3 · Review${insp.status === "complete" ? ` · ${(insp.findings || []).length} issues` : ""}` },
            { k: "checklist", label: `4 · Checklist${insp.checklist_status === "ready" ? ` · ${Object.keys(insp.checklist || {}).length} rooms` : ""}` },
            { k: "recommend", label: `5 · Recommend${insp.recommendations_status === "ready" ? ` · ${(insp.recommendations || []).length}` : ""}` },
            { k: "qa",     label: `6 · Ask` },
          ].map(t => (
            <button key={t.k} onClick={() => setTab(t.k)}
              data-testid={`insp-tab-${t.k}`}
              className={`px-3 py-2.5 border-b-2 whitespace-nowrap ${
                tab === t.k ? "border-[#00C4CC] text-[#0F1F3A]" : "border-transparent text-slate-500 hover:text-slate-800"
              }`}>{t.label}</button>
          ))}
        </nav>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-5">
        {/* IMPORT */}
        {tab === "import" && (
          <div className="space-y-4" data-testid="insp-import-panel">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              {IMPORT_METHODS.map(({ k, label, icon: Icon, hint }) => (
                <button key={k} onClick={() => setImportMethod(k)}
                  data-testid={`insp-import-method-${k}`}
                  className={`text-left p-4 rounded-2xl border-2 transition ${
                    importMethod === k ? "border-[#00C4CC] bg-white shadow-lg" : "border-slate-200 bg-white/60 hover:border-slate-300"
                  }`}>
                  <Icon size={22} weight="bold" className="text-[#00C4CC]"/>
                  <div className="mt-2 font-black text-sm">{label}</div>
                  <div className="text-[11px] text-slate-500 mt-1 leading-snug">{hint}</div>
                </button>
              ))}
            </div>

            {importMethod === "device" && (
              <div className="p-5 rounded-2xl bg-white border-2 border-dashed border-slate-300 text-center" data-testid="insp-import-device">
                <CloudArrowUp size={38} weight="thin" className="mx-auto text-slate-400"/>
                <div className="mt-2 font-black text-slate-700">Drop photos here or click to browse</div>
                <div className="text-xs text-slate-500 mt-1">Up to 200 photos · JPEG/PNG/HEIC</div>
                <input ref={fileInputRef} type="file" accept="image/*" multiple className="hidden"
                  data-testid="insp-import-file-input"
                  onChange={(e) => { uploadDevice(Array.from(e.target.files || [])); e.target.value = ""; }}/>
                <button onClick={() => fileInputRef.current?.click()} disabled={busy}
                  data-testid="insp-import-device-pick"
                  className="mt-3 h-11 px-5 rounded-lg bg-[#00C4CC] hover:bg-[#00e5ed] disabled:opacity-60 text-[#0F1F3A] font-black uppercase tracking-widest text-xs">
                  {busy ? "Uploading…" : "Choose photos"}
                </button>
              </div>
            )}
            {importMethod === "gphotos" && (
              <div className="p-5 rounded-2xl bg-white border-2 border-slate-200" data-testid="insp-import-gphotos">
                <label className="text-[10px] font-black uppercase tracking-widest text-slate-600">Google Photos share URL <span className="ml-1 text-amber-600">· Beta</span></label>
                <input value={gUrl} onChange={(e) => setGUrl(e.target.value)}
                  data-testid="insp-gphotos-url"
                  placeholder="https://photos.app.goo.gl/..."
                  className="mt-1 w-full h-11 rounded-lg border-2 border-slate-200 px-3 text-sm focus:outline-none focus:border-[#00C4CC]"/>
                <p className="text-[11px] text-slate-500 mt-2 leading-snug">
                  Google officially killed the shared-album API in March 2025. We scrape the public HTML — works today but may miss photos if Google changes the page. If it flakes, use Drive or device upload.
                </p>
                <button onClick={importGooglePhotos} disabled={busy}
                  data-testid="insp-gphotos-import"
                  className="mt-3 h-11 px-5 rounded-lg bg-[#00C4CC] hover:bg-[#00e5ed] disabled:opacity-60 text-[#0F1F3A] font-black uppercase tracking-widest text-xs">
                  {busy ? "Fetching…" : "Fetch photos"}
                </button>
              </div>
            )}
            {importMethod === "gdrive" && (
              <div className="p-5 rounded-2xl bg-white border-2 border-slate-200" data-testid="insp-import-gdrive">
                <label className="text-[10px] font-black uppercase tracking-widest text-slate-600">Google Drive folder URL</label>
                <input value={gUrl} onChange={(e) => setGUrl(e.target.value)}
                  data-testid="insp-gdrive-url"
                  placeholder="https://drive.google.com/drive/folders/..."
                  className="mt-1 w-full h-11 rounded-lg border-2 border-slate-200 px-3 text-sm focus:outline-none focus:border-[#00C4CC]"/>
                <p className="text-[11px] text-slate-500 mt-2 leading-snug">
                  Set the folder to "Anyone with the link → Viewer". Most reliable option. Requires an admin to add a Google Drive API key in backend .env (GOOGLE_DRIVE_API_KEY).
                </p>
                <button onClick={importGoogleDrive} disabled={busy}
                  data-testid="insp-gdrive-import"
                  className="mt-3 h-11 px-5 rounded-lg bg-[#00C4CC] hover:bg-[#00e5ed] disabled:opacity-60 text-[#0F1F3A] font-black uppercase tracking-widest text-xs">
                  {busy ? "Fetching…" : "Fetch photos"}
                </button>
              </div>
            )}
          </div>
        )}

        {/* TAG */}
        {tab === "tag" && (
          <div className="space-y-4" data-testid="insp-tag-panel">
            {/* Room chip bar */}
            <div className="flex flex-wrap gap-1.5 items-center">
              <div className="text-[10px] font-black uppercase tracking-widest text-slate-500 mr-2">Bulk tag untagged →</div>
              {rooms.map(r => (
                <button key={r} onClick={() => bulkTag(r)} disabled={busy || untaggedCount === 0}
                  data-testid={`insp-bulk-${r.replace(/ /g, '-')}`}
                  className="h-7 px-2.5 rounded-full bg-white border border-slate-200 hover:border-[#00C4CC] text-[11px] font-bold text-slate-700 disabled:opacity-40">
                  {r}
                </button>
              ))}
              <button onClick={addCustomRoom} data-testid="insp-add-room"
                className="h-7 px-2.5 rounded-full bg-slate-100 hover:bg-slate-200 text-[11px] font-black text-slate-600 inline-flex items-center gap-1">
                <Plus size={11} weight="bold"/> Custom
              </button>
              <div className="ml-auto flex gap-2">
                <button onClick={autoTag} disabled={autoTagging || untaggedCount === 0}
                  data-testid="insp-auto-tag"
                  title="Cheap Claude Haiku pass — only fills dropdowns with ≥70% confidence"
                  className="h-9 px-3 rounded-lg bg-white border-2 border-fuchsia-300 hover:border-fuchsia-500 disabled:opacity-40 text-fuchsia-700 font-black uppercase tracking-widest text-[11px] inline-flex items-center gap-1.5">
                  <Sparkle size={13} weight="fill"/>{autoTagging ? "Auto-tagging…" : `🪄 Auto-tag ${untaggedCount}`}
                </button>
                <button onClick={analyze} disabled={analyzing || photos.filter(p => p.room).length === 0}
                  data-testid="insp-analyze"
                  className="h-9 px-4 rounded-lg bg-fuchsia-600 hover:bg-fuchsia-700 disabled:opacity-60 text-white font-black uppercase tracking-widest text-[11px] inline-flex items-center gap-1.5">
                  <Sparkle size={13} weight="fill"/>{analyzing ? "Analyzing…" : "Analyze with AI"}
                </button>
              </div>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-2.5">
              {photos.map(p => (
                <div key={p.file_id} className="bg-white rounded-xl overflow-hidden shadow-sm border border-slate-200" data-testid={`insp-photo-${p.file_id}`}>
                  <div className="relative aspect-[4/3] bg-slate-100">
                    <img src={fileUrl(p.path)} alt="" className="w-full h-full object-cover"/>
                    <button onClick={() => setMarkupPhoto(p)}
                      data-testid={`insp-photo-markup-${p.file_id}`}
                      className="absolute top-1.5 left-1.5 h-7 w-7 rounded-full bg-black/50 hover:bg-black/70 text-white flex items-center justify-center">
                      <Pencil size={12} weight="bold"/>
                    </button>
                    <button onClick={() => removePhoto(p.file_id)}
                      data-testid={`insp-photo-delete-${p.file_id}`}
                      className="absolute top-1.5 right-1.5 h-7 w-7 rounded-full bg-black/50 hover:bg-red-600 text-white flex items-center justify-center">
                      <Trash size={12} weight="bold"/>
                    </button>
                  </div>
                  <select value={p.room || ""} onChange={(e) => tagPhoto(p.file_id, e.target.value)}
                    data-testid={`insp-photo-room-${p.file_id}`}
                    className={`w-full h-9 px-2 text-[11px] font-bold border-t ${
                      p.room && p.auto_tagged ? "bg-fuchsia-500/10 text-fuchsia-700" :
                      p.room ? "bg-[#00C4CC]/10 text-[#0e5c5f]" : "bg-amber-50 text-amber-700"
                    }`}
                    title={p.auto_tagged ? `🪄 AI ${p.auto_tag_confidence || ""}%` : ""}>
                    <option value="">— Pick room —</option>
                    {rooms.map(r => <option key={r} value={r}>{r}</option>)}
                  </select>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* REVIEW */}
        {tab === "review" && (
          <div className="space-y-5" data-testid="insp-review-panel">
            {!insp.bid_draft && (
              <div className="p-5 rounded-xl bg-white text-center text-slate-500 font-medium">
                Run AI analysis first (Tag tab → Analyze with AI).
              </div>
            )}

            {insp.bid_draft && (
              <>
                {/* Bid summary card */}
                <div className="p-5 rounded-2xl bg-white shadow flex items-start gap-4" data-testid="insp-bid-summary">
                  <div className="flex-1">
                    <div className="text-[10px] font-black uppercase tracking-widest text-slate-500">Total estimated repair scope</div>
                    <div className="text-3xl font-black text-[#0F1F3A] mt-1">${(insp.bid_draft?.total || 0).toLocaleString("en-US", { minimumFractionDigits: 2 })}</div>
                    <div className="text-xs text-slate-600 mt-2 leading-relaxed">{insp.bid_draft?.scope}</div>
                    <div className="text-[10px] text-slate-400 mt-2">AI confidence · {insp.confidence || 0}%</div>
                  </div>
                </div>

                {/* Room scores */}
                <section data-testid="insp-room-scores">
                  <div className="text-[10px] font-black uppercase tracking-widest text-slate-500 mb-2">Condition scores by room</div>
                  <div className="grid grid-cols-2 sm:grid-cols-4 md:grid-cols-6 gap-2">
                    {Object.entries(insp.room_scores || {}).map(([room, score]) => (
                      <div key={room} className="bg-white rounded-xl p-3 shadow-sm" data-testid={`insp-score-${room.replace(/ /g, '-')}`}>
                        <div className="text-[10px] font-black uppercase tracking-widest text-slate-500 truncate">{room}</div>
                        <div className={`mt-1 inline-flex items-center justify-center h-9 w-9 rounded-lg font-black text-xl ${SCORE_COLORS[score] || "bg-slate-200"}`}>{score}</div>
                      </div>
                    ))}
                  </div>
                </section>

                {/* Findings grouped by room */}
                <section data-testid="insp-findings">
                  <div className="text-[10px] font-black uppercase tracking-widest text-slate-500 mb-2">Findings · {(insp.findings || []).length} total</div>
                  <div className="space-y-3">
                    {Object.entries(findingsByRoom).map(([room, arr]) => (
                      <div key={room} className="bg-white rounded-xl shadow-sm overflow-hidden">
                        <div className="px-4 py-2 bg-slate-50 border-b border-slate-100 flex items-center gap-2">
                          <House size={13} weight="fill" className="text-slate-500"/>
                          <span className="font-black text-sm">{room}</span>
                          <span className="text-[10px] text-slate-400">· {arr.length} issue{arr.length === 1 ? "" : "s"}</span>
                        </div>
                        <div className="divide-y divide-slate-100">
                          {arr.map(f => (
                            <div key={f.finding_id} className="p-4 flex items-start gap-3" data-testid={`insp-finding-${f.finding_id}`}>
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-1.5 flex-wrap mb-1">
                                  <span className={`text-[9px] font-black uppercase tracking-widest px-1.5 py-0.5 rounded ${CATEGORY_COLORS[f.category] || CATEGORY_COLORS.other}`}>{f.category}</span>
                                  <span className={`text-[9px] font-black uppercase tracking-widest px-1.5 py-0.5 rounded ${SEVERITY_COLORS[f.severity] || SEVERITY_COLORS.minor}`}>{f.severity}</span>
                                  <span className="text-[10px] text-slate-400 ml-auto">${(f.est_cost || 0).toLocaleString("en-US", { minimumFractionDigits: 2 })}</span>
                                </div>
                                <div className="text-sm text-slate-800 leading-snug">{f.description}</div>
                                {f.photo_file_ids?.length > 0 && (
                                  <div className="flex gap-1 mt-2 overflow-x-auto">
                                    {f.photo_file_ids.map(fid => {
                                      const ph = photos.find(x => x.file_id === fid);
                                      return ph ? <img key={fid} src={fileUrl(ph.path)} alt="" className="h-14 w-14 rounded object-cover flex-shrink-0"/> : null;
                                    })}
                                  </div>
                                )}
                              </div>
                              {!f.wo_id ? (
                                <button onClick={() => spawnWO(f.finding_id)} data-testid={`insp-finding-spawn-${f.finding_id}`}
                                  className="h-8 px-3 rounded-lg bg-[#00C4CC] hover:bg-[#00e5ed] text-[#0F1F3A] font-black uppercase tracking-widest text-[10px]">
                                  → WO
                                </button>
                              ) : (
                                <a href={`/admin/jobs/${f.wo_id}`} target="_blank" rel="noreferrer" data-testid={`insp-finding-wo-${f.finding_id}`}
                                  className="h-8 px-3 rounded-lg bg-emerald-100 text-emerald-700 font-black uppercase tracking-widest text-[10px] inline-flex items-center">
                                  ✓ WO
                                </a>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                    {(insp.findings || []).length === 0 && (
                      <div className="p-8 rounded-xl bg-white text-center text-slate-500 font-medium">
                        AI didn't find any issues. That's a good property.
                      </div>
                    )}
                  </div>
                </section>

                {/* Appliances */}
                {(insp.appliances || []).length > 0 && (
                  <section data-testid="insp-appliances">
                    <div className="text-[10px] font-black uppercase tracking-widest text-slate-500 mb-2">Appliance nameplates · {(insp.appliances || []).length} detected</div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {insp.appliances.map(a => (
                        <div key={a.id} className="bg-white rounded-xl p-4 shadow-sm flex items-start gap-3" data-testid={`insp-appliance-${a.id}`}>
                          {a.photo_file_id && (() => {
                            const ph = photos.find(x => x.file_id === a.photo_file_id);
                            return ph ? <img src={fileUrl(ph.path)} alt="" className="h-16 w-16 rounded-lg object-cover flex-shrink-0"/> : null;
                          })()}
                          <div className="flex-1 min-w-0">
                            <div className="text-[10px] font-black uppercase tracking-widest text-slate-500">{a.room} · {a.type}</div>
                            <div className="font-black text-sm">{a.brand} {a.model}</div>
                            {a.serial && <div className="text-xs text-slate-500 font-mono truncate">SN {a.serial}</div>}
                            {a.age_years != null && <div className="text-xs text-slate-500 mt-0.5">~{a.age_years} yrs old</div>}
                            {a.notes && <div className="text-xs text-slate-600 mt-1 leading-snug">{a.notes}</div>}
                          </div>
                        </div>
                      ))}
                    </div>
                  </section>
                )}
              </>
            )}
          </div>
        )}

        {/* CHECKLIST — AI-drafted grid PM confirms row-by-row */}
        {tab === "checklist" && (
          <div className="space-y-4" data-testid="insp-checklist-panel">
            {(!insp.checklist || Object.keys(insp.checklist).length === 0) ? (
              <div className="p-6 rounded-xl bg-white text-center">
                <div className="text-sm text-slate-600 mb-3 leading-relaxed">
                  AI grades each room on 6 items (walls, ceiling, floor, windows, outlets, smoke detector) and answers the yes/no PM checks straight from your photos. You review and flip anything wrong.
                </div>
                <button onClick={genChecklist} disabled={checklistBusy || photos.filter(p => p.room).length === 0}
                  data-testid="insp-checklist-generate"
                  className="h-11 px-5 rounded-lg bg-fuchsia-600 hover:bg-fuchsia-700 disabled:opacity-60 text-white font-black uppercase tracking-widest text-xs inline-flex items-center gap-2">
                  <Sparkle size={14} weight="fill"/>{checklistBusy ? "Drafting…" : "Draft checklist with AI"}
                </button>
              </div>
            ) : (
              <>
                <div className="flex items-center gap-2">
                  <div className="text-[10px] font-black uppercase tracking-widest text-slate-500">Tap any row to confirm or override</div>
                  <button onClick={genChecklist} disabled={checklistBusy}
                    data-testid="insp-checklist-regen"
                    className="ml-auto h-8 px-3 rounded-full bg-white border border-slate-200 hover:border-fuchsia-500 text-[10px] font-black uppercase tracking-widest text-slate-700">
                    {checklistBusy ? "…" : "Re-draft"}
                  </button>
                </div>
                {Object.entries(insp.checklist).map(([room, items]) => (
                  <details key={room} open className="bg-white rounded-xl shadow-sm overflow-hidden" data-testid={`insp-checklist-room-${room.replace(/ /g, '-')}`}>
                    <summary className="px-4 py-2.5 bg-slate-50 border-b border-slate-100 flex items-center gap-2 cursor-pointer">
                      <House size={13} weight="fill" className="text-slate-500"/>
                      <span className="font-black text-sm">{room}</span>
                    </summary>
                    <div className="divide-y divide-slate-100">
                      {Object.entries(items).map(([item, cell]) => (
                        <ChecklistRow key={item} room={room} item={item} cell={cell} onChange={patchCell}/>
                      ))}
                    </div>
                  </details>
                ))}
              </>
            )}
          </div>
        )}

        {/* RECOMMENDATIONS — AI-drafted turnover action list */}
        {tab === "recommend" && (
          <div className="space-y-4" data-testid="insp-recommend-panel">
            {(!insp.recommendations || insp.recommendations.length === 0) ? (
              <div className="p-6 rounded-xl bg-white text-center">
                <div className="text-sm text-slate-600 mb-3 leading-relaxed">
                  AI produces a make-ready-crew action list — "Paint all walls", "Paint trim", "Re-caulk tub", etc — grouped, prioritized, with rough US retail pricing.
                </div>
                <button onClick={genRecs} disabled={recsBusy || photos.filter(p => p.room).length === 0}
                  data-testid="insp-recommend-generate"
                  className="h-11 px-5 rounded-lg bg-fuchsia-600 hover:bg-fuchsia-700 disabled:opacity-60 text-white font-black uppercase tracking-widest text-xs inline-flex items-center gap-2">
                  <Sparkle size={14} weight="fill"/>{recsBusy ? "Drafting…" : "Draft turnover actions"}
                </button>
              </div>
            ) : (
              <>
                <div className="flex items-center">
                  <div className="text-[10px] font-black uppercase tracking-widest text-slate-500">
                    Total est turnover: ${(insp.recommendations || []).reduce((n, r) => n + (r.est_cost || 0), 0).toLocaleString("en-US", { minimumFractionDigits: 2 })}
                  </div>
                  <button onClick={genRecs} disabled={recsBusy}
                    data-testid="insp-recommend-regen"
                    className="ml-auto h-8 px-3 rounded-full bg-white border border-slate-200 hover:border-fuchsia-500 text-[10px] font-black uppercase tracking-widest text-slate-700">
                    {recsBusy ? "…" : "Re-draft"}
                  </button>
                </div>
                <div className="space-y-2">
                  {insp.recommendations.map((r) => (
                    <div key={r.id} className="bg-white rounded-xl shadow-sm p-4 flex items-start gap-3" data-testid={`insp-rec-${r.id}`}>
                      <span className={`h-6 w-6 rounded-full flex-shrink-0 flex items-center justify-center text-[10px] font-black uppercase tracking-widest ${
                        r.priority === "high" ? "bg-red-100 text-red-700" :
                        r.priority === "low" ? "bg-slate-100 text-slate-600" : "bg-amber-100 text-amber-700"
                      }`}>{(r.priority || "med")[0].toUpperCase()}</span>
                      <div className="flex-1 min-w-0">
                        <div className="font-black text-sm">{r.action}</div>
                        <div className="text-[11px] text-slate-500 mt-0.5">
                          {(r.rooms || []).join(", ") || "—"} · {r.labor_hours}h · ${(r.est_cost || 0).toLocaleString("en-US", { minimumFractionDigits: 2 })}
                        </div>
                        {r.why && <div className="text-[12px] text-slate-600 mt-1 leading-snug">{r.why}</div>}
                      </div>
                      {r.wo_id ? (
                        <a href={`/admin/jobs/${r.wo_id}`} target="_blank" rel="noreferrer"
                          data-testid={`insp-rec-wo-${r.id}`}
                          className="h-8 px-3 rounded-lg bg-emerald-100 text-emerald-700 font-black uppercase tracking-widest text-[10px] inline-flex items-center">
                          ✓ WO
                        </a>
                      ) : (
                        <button onClick={() => spawnRecWO(r.id)}
                          data-testid={`insp-rec-spawn-${r.id}`}
                          className="h-8 px-3 rounded-lg bg-[#00C4CC] hover:bg-[#00e5ed] text-[#0F1F3A] font-black uppercase tracking-widest text-[10px]">
                          → WO
                        </button>
                      )}
                    </div>
                  ))}
                </div>

                {/* Complete Inspection block — closes the loop */}
                <div className="mt-6 rounded-2xl border-2 border-dashed border-slate-300 bg-white p-5" data-testid="insp-complete-block">
                  {insp.status === "completed" ? (
                    <div className="space-y-3">
                      <div className="flex items-center gap-2">
                        <CheckCircle size={20} weight="fill" className="text-emerald-500"/>
                        <div className="flex-1">
                          <div className="font-black text-sm text-[#0F1F3A]">Inspection completed</div>
                          <div className="text-[11px] text-slate-500">
                            v{insp.report_version || 1}
                            {insp.completed_at ? ` · ${new Date(insp.completed_at).toLocaleString()}` : ""}
                            {insp.completed_by_name ? ` · ${insp.completed_by_name}` : ""}
                          </div>
                        </div>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        <button onClick={openReport}
                          data-testid="insp-view-report-pdf"
                          className="h-10 px-4 rounded-lg bg-[#0F1F3A] hover:bg-[#1a2f52] text-white font-black uppercase tracking-widest text-[11px] inline-flex items-center gap-2">
                          <FilePdf size={14} weight="fill"/> View Full PDF
                        </button>
                        <button onClick={copyShareLink}
                          data-testid="insp-copy-share-link"
                          className="h-10 px-4 rounded-lg bg-white border-2 border-slate-200 hover:border-[#00C4CC] text-[#0F1F3A] font-black uppercase tracking-widest text-[11px] inline-flex items-center gap-2">
                          <LinkSimple size={14} weight="bold"/> Copy Public Link
                        </button>
                        <button onClick={emailLandlord}
                          data-testid="insp-email-landlord"
                          className="h-10 px-4 rounded-lg bg-white border-2 border-slate-200 hover:border-[#00C4CC] text-[#0F1F3A] font-black uppercase tracking-widest text-[11px] inline-flex items-center gap-2">
                          <Envelope size={14} weight="bold"/> Email Landlord
                        </button>
                        <button onClick={completeInspection} disabled={completing}
                          data-testid="insp-resnapshot"
                          className="h-10 px-4 rounded-lg bg-white border-2 border-slate-200 hover:border-slate-400 text-slate-600 font-black uppercase tracking-widest text-[11px] inline-flex items-center gap-2 ml-auto">
                          {completing ? "…" : "Re-snapshot"}
                        </button>
                      </div>
                      {shareUrl && (
                        <div className="text-[10px] font-mono text-slate-500 break-all bg-slate-50 rounded px-2 py-1.5" data-testid="insp-share-url">
                          {shareUrl}
                        </div>
                      )}
                      <div className="text-[10px] text-slate-500 leading-snug">
                        Public link shows a sanitized version — photos, room grades, and recommended work (no cost, no hours, no admin names). The full PDF stays admin-only.
                      </div>
                    </div>
                  ) : (
                    <div className="text-center space-y-3">
                      <div className="font-black text-sm text-[#0F1F3A]">Ready to finalize?</div>
                      <div className="text-[12px] text-slate-500 leading-snug max-w-md mx-auto">
                        Mark this inspection complete to lock in a report snapshot, generate the PDF, and mint a shareable landlord link. You can still edit findings and re-snapshot anytime.
                      </div>
                      <button onClick={completeInspection} disabled={completing}
                        data-testid="insp-complete-btn"
                        className="h-11 px-6 rounded-lg bg-emerald-600 hover:bg-emerald-700 disabled:opacity-60 text-white font-black uppercase tracking-widest text-xs inline-flex items-center gap-2">
                        <CheckCircle size={14} weight="fill"/>
                        {completing ? "Completing…" : "Complete Inspection"}
                      </button>
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        )}

        {/* Q&A */}
        {tab === "qa" && (
          <div className="max-w-3xl mx-auto space-y-4" data-testid="insp-qa-panel">
            {(insp.qa_thread || []).length === 0 && (
              <div className="p-6 rounded-xl bg-white text-center text-slate-500 font-medium">
                Ask questions like "any signs of water damage in the bathrooms?" or "what shape is the kitchen floor in?"
              </div>
            )}
            <div className="space-y-3">
              {(insp.qa_thread || []).map((qa, i) => (
                <div key={i} className="space-y-2" data-testid={`insp-qa-${i}`}>
                  <div className="bg-[#00C4CC] text-[#0F1F3A] rounded-2xl rounded-tl-none px-4 py-2.5 max-w-[85%] font-medium text-sm">
                    <div className="text-[10px] font-black uppercase tracking-widest opacity-60 mb-0.5">You</div>
                    {qa.q}
                  </div>
                  <div className="bg-white rounded-2xl rounded-tr-none px-4 py-2.5 max-w-[85%] ml-auto shadow-sm text-sm">
                    <div className="text-[10px] font-black uppercase tracking-widest text-slate-500 mb-0.5 flex items-center gap-1"><ChatCircleDots size={11} weight="fill"/> AI · {qa.photos_used} photos</div>
                    {qa.a}
                  </div>
                </div>
              ))}
            </div>
            <form onSubmit={askAI} className="sticky bottom-4 bg-white rounded-xl shadow-lg p-2 flex items-center gap-2" data-testid="insp-qa-form">
              <Question size={16} className="text-slate-400 ml-2"/>
              <input value={ask} onChange={(e) => setAsk(e.target.value)}
                data-testid="insp-qa-input"
                placeholder={insp.status === "complete" ? "Ask about the photos…" : "Analyze the inspection first"}
                disabled={insp.status !== "complete" || asking}
                className="flex-1 h-11 px-2 text-sm focus:outline-none disabled:opacity-40"/>
              <button type="submit" disabled={!ask.trim() || asking || insp.status !== "complete"}
                data-testid="insp-qa-submit"
                className="h-9 px-3 rounded-lg bg-[#00C4CC] hover:bg-[#00e5ed] disabled:opacity-40 text-[#0F1F3A] font-black uppercase tracking-widest text-[10px] inline-flex items-center gap-1">
                <PaperPlaneTilt size={12} weight="fill"/> {asking ? "…" : "Ask"}
              </button>
            </form>
          </div>
        )}
      </main>

      {/* Photo markup reuses the existing modal — inspection photos live in
          the inspections collection, not jobs, but the modal only needs a
          jobId for its upload. Passing the inspection id here means the
          markup export gets saved as a new photo on the inspection. */}
      {markupPhoto && (
        <PhotoMarkupModal
          src={fileUrl(markupPhoto.path)}
          jobId={iid}
          uploadEndpoint={`/prop-inspections/${iid}/photo`}
          onClose={() => setMarkupPhoto(null)}
          onSaved={() => { setMarkupPhoto(null); load(); }}
        />
      )}
    </div>
  );
}

function ChecklistRow({ room, item, cell, onChange }) {
  const cond = cell.condition || "na";
  const CONDS = [
    { k: "good", label: "Good", cls: "bg-emerald-500 text-white" },
    { k: "fair", label: "Fair", cls: "bg-amber-400 text-slate-900" },
    { k: "poor", label: "Poor", cls: "bg-red-500 text-white" },
    { k: "na",   label: "N/A",  cls: "bg-slate-200 text-slate-600" },
  ];
  return (
    <div className={`p-3 ${cell.confirmed ? "bg-emerald-50/40" : ""}`} data-testid={`insp-cl-${room.replace(/ /g, '-')}-${item.replace(/ /g, '-')}`}>
      <div className="flex items-center gap-2 mb-2">
        <span className="font-black text-sm flex-1">{item}</span>
        {cell.checks?.some(c => c.confidence < 50) && (
          <span className="text-[9px] font-black uppercase tracking-widest px-1.5 py-0.5 rounded bg-amber-100 text-amber-700">Low confidence · verify</span>
        )}
        <button onClick={() => onChange(room, item, { flagged_for_turn: !cell.flagged_for_turn })}
          data-testid={`insp-cl-flag-${item.replace(/ /g, '-')}`}
          className={`text-[10px] font-black uppercase tracking-widest px-2 py-1 rounded ${
            cell.flagged_for_turn ? "bg-red-500 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"
          }`}>
          🚩 {cell.flagged_for_turn ? "Flagged" : "Flag"}
        </button>
      </div>
      <div className="flex gap-1.5 mb-2">
        {CONDS.map(c => (
          <button key={c.k} onClick={() => onChange(room, item, { condition: c.k, confirmed: true })}
            data-testid={`insp-cl-cond-${item.replace(/ /g, '-')}-${c.k}`}
            className={`flex-1 h-9 rounded-lg font-black uppercase tracking-widest text-[10px] transition ${
              cond === c.k ? c.cls : "bg-white border border-slate-200 text-slate-500 hover:border-slate-400"
            }`}>{c.label}</button>
        ))}
      </div>
      <div className="space-y-1">
        {(cell.checks || []).map((chk, i) => (
          <div key={i} className="flex items-center gap-2 text-[13px]">
            <span className="flex-1 text-slate-700">{chk.q}</span>
            {["yes", "no", "na"].map(v => (
              <button key={v} onClick={() => {
                const nx = [...cell.checks];
                nx[i] = { ...nx[i], a: v };
                onChange(room, item, { checks: nx, confirmed: true });
              }}
                data-testid={`insp-cl-check-${item.replace(/ /g, '-')}-${i}-${v}`}
                className={`h-7 px-2.5 rounded-full text-[10px] font-black uppercase tracking-widest ${
                  chk.a === v ?
                    (v === "yes" ? "bg-emerald-500 text-white" :
                     v === "no" ? "bg-red-500 text-white" : "bg-slate-400 text-white") :
                    "bg-white border border-slate-200 text-slate-500 hover:border-slate-400"
                }`}>{v.toUpperCase()}</button>
            ))}
          </div>
        ))}
      </div>
      <input type="text" value={cell.note || ""}
        onChange={(e) => onChange(room, item, { note: e.target.value })}
        placeholder="Note (optional)"
        data-testid={`insp-cl-note-${item.replace(/ /g, '-')}`}
        className="mt-2 w-full h-8 px-2 text-[12px] rounded border border-slate-200 focus:outline-none focus:border-[#00C4CC]"/>
    </div>
  );
}

function StatusPill({ status }) {
  const map = {
    draft:    "bg-slate-200 text-slate-700",
    tagging:  "bg-amber-100 text-amber-700",
    analyzing:"bg-purple-100 text-purple-700 animate-pulse",
    complete: "bg-emerald-100 text-emerald-700",
    completed:"bg-emerald-500 text-white",
  };
  return (
    <span className={`text-[9px] font-black uppercase tracking-widest px-2 py-1 rounded ${map[status] || map.draft}`} data-testid="insp-status">
      {status || "draft"}
    </span>
  );
}
